import { uiLogger } from "../../utils/logger"; import { useState, useRef, useEffect, JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChatBubbleLeftRightIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import useAppStore from '../../stores/useAppStore';
import { authApi } from '../../services/api';
import { authService } from '../../services/auth';

export default function SimpleUserDropdown(): JSX.Element {
  const navigate = useNavigate();
  const { user, logout } = useAppStore();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleApp = async (): Promise<void> => {
    try {
      const token = await authService.getJwtToken();
      const refreshToken = await authService.getRefreshToken();
      const adminBaseUrl = import.meta.env.VITE_ADMIN_BASE_URL;
      
      if (token && adminBaseUrl) {
        let appUrl = `${adminBaseUrl}?token=${token}`;
        if (refreshToken) {
          appUrl += `&refreshToken=${refreshToken}`;
        }
        console.log('Opening App URL:', appUrl);
        window.location.href = appUrl;
      } else {
        uiLogger.error('Missing token or admin base URL');
      }
      
      setIsOpen(false);
    } catch (error) {
      uiLogger.error('App navigation error:', error);
    }
  };

  const handleLogout = async (): Promise<void> => {
    try {
      // Sign out from Cognito and clear session
      await authApi.logout();

      // Update local state
      logout();
      setIsOpen(false);

      uiLogger.info('✅ User logged out successfully');
    } catch (error) {
      uiLogger.error('❌ Logout error:', error);

      // Even if remote logout fails, clear local state
      logout();
      setIsOpen(false);
    }
  };

  const handleAccount = (): void => {
    try {
      uiLogger.info('Opening account settings...');
      setIsOpen(false);
    } catch (error) {
      uiLogger.error('Account error:', error);
    }
  };

  const handleContactUs = (): void => {
    try {
      const userRole = user?.role || 'User';
      
      if (userRole === 'Admin') {
        // Navigate to admin contact page for Admin users
        navigate('/admin/contact');
      } else {
        // Navigate to coming-soon page for regular users
        navigate('/coming-soon');
      }
      
      setIsOpen(false);
    } catch (error) {
      uiLogger.error('Navigation error:', error);
      // Fallback based on role
      const userRole = user?.role || 'User';
      if (userRole === 'Admin') {
        window.location.href = '/admin/contact';
      } else {
        window.location.href = '/coming-soon';
      }
    }
  };

  const handleAdmin = (): void => {
    try {
      navigate('/admin');
      setIsOpen(false);
    } catch (error) {
      uiLogger.error('Navigation error:', error);
      window.location.href = '/admin';
    }
  };

  const toggleDropdown = (): void => {
    setIsOpen(!isOpen);
  };

  // Get user initials for avatar
  const getInitials = (name?: string): string => {
    try {
      // Use provided initials if available
      if (user?.initials) {
        return user.initials;
      }

      // Fallback to generating from name
      if (!name) return 'U';
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    } catch (error) {
      return 'U';
    }
  };

  const userName = user?.name || 'User';
  const userInitials = getInitials(userName);
  const shouldUseIcon = user?.useIcon || false;
  const userRole = user?.role || 'User';
  const isAdmin = userRole === 'Admin';

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        data-testid="user-dropdown-trigger"
        onClick={toggleDropdown}
        className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
      >
        {/* User Avatar */}
        <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
          {shouldUseIcon ? (
            <UserCircleIcon className="h-5 w-5" />
          ) : (
            userInitials
          )}
        </div>

        {/* Dropdown Arrow */}
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          data-testid="user-dropdown-menu"
          className="absolute right-0 mt-2 w-48 origin-top-right divide-y divide-gray-100 dark:divide-gray-700 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
        >
          {/* User Info Section */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            {user?.name === 'Unknown' ? (
              // When name is 'Unknown', show only email address in bold
              <p data-testid="user-name-display" className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {user?.email || 'demo@fabrik28.ai'}
              </p>
            ) : (
              // Normal case: show name and email separately
              <>
                <p data-testid="user-name-display" className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {user?.name || 'Demo User'}
                </p>
                <p data-testid="user-email-display" className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user?.email || 'demo@fabrik28.ai'}
                </p>
              </>
            )}
          </div>

          {/* Menu Items based on user role */}
          <div className="py-1">
            <button
              data-testid="app-button"
              onClick={handleApp}
              className="group flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200"
            >
              <RocketLaunchIcon className="mr-3 h-4 w-4" aria-hidden="true" />
              App
            </button>
            
            <button
              data-testid="contact-us-button"
              onClick={handleContactUs}
              className="group flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200"
            >
              {isAdmin ? (
                <ChatBubbleLeftRightIcon className="mr-3 h-4 w-4" aria-hidden="true" />
              ) : (
                <InformationCircleIcon className="mr-3 h-4 w-4" aria-hidden="true" />
              )}
              Contact Us
            </button>
          </div>

          {/* Logout (separated) */}
          <div className="py-1">
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="group flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200"
            >
              <ArrowRightOnRectangleIcon className="mr-3 h-4 w-4" aria-hidden="true" />
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
